'use client'

/**
 * CustomerJourneyFlow — a single customer's path to purchase rendered as a
 * horizontal, channel-colored sankey-style band. Touchpoints flow left → right
 * into a final Purchase node. Ribbons gradient between adjacent channel colors.
 *
 * Used in the journey modal (compact `md`) and the customer detail page (`lg`).
 * End-station labels are start/end-anchored and the viewBox is padded so the
 * first/last labels never clip inside a tight container (e.g. the modal).
 */

import { useState } from 'react'
import {
  JOURNEY_CHANNEL_COLOR,
  JOURNEY_CHANNEL_LABEL,
  fmtUsd,
  type ExampleCustomer,
} from '@/lib/customers/customer-row'

const PURCHASE_COLOR = '#0A8A6E'

export function CustomerJourneyFlow({
  customer,
  size = 'md',
}: {
  customer: ExampleCustomer
  size?: 'md' | 'lg'
}) {
  const [hover, setHover] = useState<number | null>(null)

  const lg = size === 'lg'
  const bandH = lg ? 50 : 36
  const nodeW = 13
  const titleSize = lg ? 13 : 11.5
  const minSegW = lg ? 150 : 104
  const charW = titleSize * 0.62
  const stations = [
    ...customer.journey.map((t) => ({
      color: JOURNEY_CHANNEL_COLOR[t.channel],
      title: t.label,
      sub: `Day ${t.day}`,
      channelLabel: JOURNEY_CHANNEL_LABEL[t.channel],
    })),
    {
      color: PURCHASE_COLOR,
      title: 'Purchased',
      sub: fmtUsd(customer.totalSpentCents),
      channelLabel: 'Conversion',
    },
  ]
  const segW = Math.max(
    minSegW,
    ...stations.slice(0, -1).map((s, i) => {
      const left = s.title.length * charW
      const right = stations[i + 1].title.length * charW
      return left / 2 + right / 2 + 36
    }),
  )
  const padTop = lg ? 44 : 40
  const padBottom = lg ? 32 : 28
  const padX = lg ? 60 : 52
  const chanSize = lg ? 10 : 9.5
  const subSize = lg ? 11.5 : 10.5

  const N = stations.length
  const W = (N - 1) * segW + nodeW + padX * 2
  const H = padTop + bandH + padBottom
  const yTop = padTop
  const xOf = (i: number) => padX + i * segW

  // First label left-anchored, last right-anchored, middle centered — keeps
  // end labels inside the viewBox.
  const anchorFor = (i: number): 'start' | 'middle' | 'end' =>
    i === 0 ? 'start' : i === N - 1 ? 'end' : 'middle'
  const labelXFor = (i: number) =>
    i === 0 ? xOf(i) : i === N - 1 ? xOf(i) + nodeW : xOf(i) + nodeW / 2

  return (
    <div className="-mx-1 overflow-x-auto px-1 [scrollbar-width:thin]">
      <div style={{ minWidth: W }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          className="h-auto"
          role="img"
          aria-label={`Path to purchase for ${customer.anonId}`}
          onMouseLeave={() => setHover(null)}
        >
          <defs>
            {stations.slice(0, -1).map((s, i) => (
              <linearGradient key={i} id={`jf-${customer.id}-${i}`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={s.color} />
                <stop offset="100%" stopColor={stations[i + 1].color} />
              </linearGradient>
            ))}
          </defs>

          {/* Ribbons between stations */}
          {stations.slice(0, -1).map((s, i) => {
            const x0 = xOf(i) + nodeW
            const x1 = xOf(i + 1)
            const dim = hover !== null && hover !== i && hover !== i + 1
            return (
              <rect
                key={`rib-${i}`}
                x={x0}
                y={yTop + bandH * 0.18}
                width={x1 - x0}
                height={bandH * 0.64}
                fill={`url(#jf-${customer.id}-${i})`}
                opacity={dim ? 0.18 : 0.6}
                style={{ transition: 'opacity 140ms ease' }}
              />
            )
          })}

          {/* Station nodes + labels */}
          {stations.map((s, i) => {
            const x = xOf(i)
            const dim = hover !== null && hover !== i
            const isPurchase = i === N - 1
            const anchor = anchorFor(i)
            const lx = labelXFor(i)
            return (
              <g key={`st-${i}`} onMouseEnter={() => setHover(i)} style={{ cursor: 'default' }}>
                <rect
                  x={x}
                  y={yTop}
                  width={nodeW}
                  height={bandH}
                  rx={5}
                  fill={s.color}
                  opacity={dim ? 0.4 : 1}
                  style={{ transition: 'opacity 140ms ease' }}
                >
                  <title>{`${s.title} · ${s.channelLabel} · ${s.sub}`}</title>
                </rect>
                <text
                  x={lx}
                  y={yTop - 29}
                  textAnchor={anchor}
                  fontSize={titleSize}
                  fontWeight={700}
                  style={{ fill: 'var(--color-atrium-black)' }}
                  opacity={dim ? 0.4 : 1}
                >
                  {s.title}
                </text>
                <text
                  x={lx}
                  y={yTop - 10}
                  textAnchor={anchor}
                  fontSize={chanSize}
                  fontWeight={600}
                  fill={s.color}
                  opacity={dim ? 0.4 : 1}
                  style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
                >
                  {s.channelLabel}
                </text>
                <text
                  x={lx}
                  y={yTop + bandH + 17}
                  textAnchor={anchor}
                  fontSize={subSize}
                  fontWeight={isPurchase ? 800 : 500}
                  style={isPurchase ? { fill: PURCHASE_COLOR } : { fill: 'var(--color-ink-3)' }}
                  opacity={dim ? 0.4 : 1}
                >
                  {s.sub}
                </text>
              </g>
            )
          })}
        </svg>
      </div>
    </div>
  )
}
